import React from 'react';
import Translate from '../Translate/Translate';
import { observer } from 'mobx-react';
import { gql } from '@apollo/client';
import Loader from '../Loader/Loader';
import { useQuery } from '@apollo/client/react';

const GET_SHOPS = gql`
  query PublicShops {
    publicShops {
      id
      title
      slug
      city
      citySlug
    }
  }
`;
type ShopItem = {
  id: string;
  title: string;
  slug: string;
  city: string;
  info: string;
  citySlug: string;
};

type Quantity = {
  quantity: number;
  shop_id: number;
  variation: {
    code: string;
  };
};

type Variation = {
  value: string;
  code: string;
};

interface Props {
  quantities: Quantity[];
  variations: Variation[];
}

const Availability: React.FC<Props> = observer(({ quantities, variations }) => {
  const { loading, error, data } = useQuery<{
    publicShops: ShopItem[];
  }>(GET_SHOPS);

  const shops = data?.publicShops;

  if (loading) {
    return (
      <div className="flex justify-center m-10">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <div className="m-5">Error: {error.message}</div>;
  }

  const shopVariations = (
    selectedShopId: number | undefined,
    title: string,
    last: boolean,
  ) => {
    let allShopIds = shops.map((shop) => parseInt(shop.id));
    if (selectedShopId) {
      allShopIds = allShopIds.filter((shopId) => shopId === selectedShopId);
    }
    let hasAvailability = false;

    if (!allShopIds.length) {
      return null;
    }

    const availabilities = variations.map((v) => {
      const quantity = quantities.find(
        (q) =>
          (allShopIds.includes(q.shop_id) || selectedShopId === undefined) &&
          q.variation.code === v.code &&
          q.quantity === 1,
      );

      let check = null;
      if (quantity) {
        check = (
          <span className="bg-rgreen inline-block h-2 w-2 rounded-[50%]" />
        );
        hasAvailability = true;
      }
      return (
        <td
          className="border w-15 border-solid border-rgrey-light text-center px-4 py-2"
          key={v.code}
        >
          {check}
        </td>
      );
    });

    if (!hasAvailability) {
      return null;
    }

    return (
      // yhe poe kohta rida
      <tr key={allShopIds.join()} className={last ? 'shop last' : 'shop'}>
        <td className="px-4 py-2 text-left border sticky z-9998 border-solid border-rgrey-light left-0 bg-rgrey-lightlight">
          {/* poe nimi */}
          <div className="w-full">{title}</div>
        </td>
        {availabilities}
      </tr>
    );
  };

  const variationsHeader = variations.map((variation) => (
    <th
      className="sticky z-9999 text-center h-auto w-15 p-2 top-0 bg-rgrey-lightlight font-axi-bold"
      key={variation.code}
    >
      {/* Suurused */}

      {variation.value}
    </th>
  ));

  const rocca = shopVariations(3, 'Rocca al Mare', false);
  const ylemiste = shopVariations(15, 'Ülemiste', false);
  const kristiine = shopVariations(14, 'Kristiine', false);
  const jarve = shopVariations(4, 'Järve', false);
  const t1 = shopVariations(21, 'T1 outlet', false);
  const nautica = shopVariations(19, 'Nautica', false);
  const magistral = shopVariations(1, 'Magistral outlet', true);
  const inTallinn =
    rocca || ylemiste || kristiine || jarve || t1 || nautica || magistral;

  const lounakas = shopVariations(6, 'Lõunakeskus', false);
  const kvartal = shopVariations(7, 'Kvartal', false);
  const eeden = shopVariations(17, 'Eeden outlet', true);
  const inTartu = lounakas || kvartal || eeden;

  const kaubamajakas = shopVariations(2, 'Kaubamajakas', false);
  const portartur = shopVariations(9, 'Port Artur 2', true);
  const inParnu = kaubamajakas || portartur;

  const pohjakeskus = shopVariations(12, 'Põhjakeskus', true);
  const inRakvere = pohjakeskus;

  const fama = shopVariations(5, 'Fama', true);
  const inNarva = fama;

  const pargi = shopVariations(11, 'Pargi', true);
  const inJohvi = pargi;

  const centrum = shopVariations(8, 'Centrum', true);
  const inViljandi = centrum;

  const auriga = shopVariations(13, 'Auriga', true);
  const inKuressaare = auriga;

  const rannarootsi = shopVariations(10, 'Rannarootsi', true);
  const inHaapsalu = rannarootsi;

  return (
    <div
      className="availability overflow-auto min-w-137.5 max-h-125 mb-2"
      style={{ width: '100%' }}
    >
      <table className="w-full table-auto">
        <thead>
          <tr>
            <th className="text-center h-auto p-2 top-0 bg-rgrey-lightlight font-axi-bold w-36 sticky z-10000 left-0">
              {/* Pealkiri - suurused */}
              <Translate component="availability" keyword="sizes" />
            </th>
            {variationsHeader}
          </tr>
        </thead>
        <tbody>
          <>
            {shopVariations(undefined, 'Saadavus', false)}
            {inTallinn && (
              <tr>
                <td className="text-rgreen text-xs text-left sticky z-9998 pl-2 pr-4 pt-4 pb-2 left-0 font-axi-bold">
                  Tallinn
                </td>
              </tr>
            )}
            {shopVariations(3, 'Rocca al Mare', false)}
            {shopVariations(15, 'Ülemiste', false)}
            {shopVariations(14, 'Kristiine', false)}
            {shopVariations(4, 'Järve', false)}
            {shopVariations(21, 'T1 outlet', false)}
            {shopVariations(19, 'Nautica', false)}
            {shopVariations(1, 'Magistral outlet', true)}
            {inTartu && (
              <tr>
                <td className="text-rgreen text-xs text-left sticky z-9998 pl-2 pr-4 pt-4 pb-2 left-0 font-axi-bold">
                  Tartu
                </td>
              </tr>
            )}
            {shopVariations(6, 'Lõunakeskus', false)}
            {shopVariations(7, 'Kvartal', false)}
            {shopVariations(17, 'Eeden outlet', true)}
            {inParnu && (
              <tr>
                <td className="text-rgreen text-xs text-left sticky z-9998 pl-2 pr-4 pt-4 pb-2 left-0 font-axi-bold">
                  Pärnu
                </td>
              </tr>
            )}
            {shopVariations(2, 'Kaubamajakas', false)}
            {shopVariations(9, 'Port Artur 2', true)}
            {inRakvere && (
              <tr>
                <td className="text-rgreen text-xs text-left sticky z-9998 pl-2 pr-4 pt-4 pb-2 left-0 font-axi-bold">
                  Rakvere
                </td>
              </tr>
            )}
            {shopVariations(12, 'Põhjakeskus', true)}
            {inNarva && (
              <tr>
                <td className="text-rgreen text-xs text-left sticky z-9998 pl-2 pr-4 pt-4 pb-2 left-0 font-axi-bold">
                  Narva
                </td>
              </tr>
            )}
            {shopVariations(5, 'Fama', true)}
            {inJohvi && (
              <tr>
                <td className="text-rgreen text-xs text-left sticky z-9998 pl-2 pr-4 pt-4 pb-2 left-0 font-axi-bold">
                  Jõhvi
                </td>
              </tr>
            )}
            {shopVariations(11, 'Pargi', true)}
            {inViljandi && (
              <tr>
                <td className="text-rgreen text-xs text-left sticky z-9998 pl-2 pr-4 pt-4 pb-2 left-0 font-axi-bold">
                  Viljandi
                </td>
              </tr>
            )}
            {shopVariations(8, 'Centrum', true)}
            {inKuressaare && (
              <tr>
                <td className="text-rgreen text-xs text-left sticky z-9998 pl-2 pr-4 pt-4 pb-2 left-0 font-axi-bold">
                  Kuressaare
                </td>
              </tr>
            )}
            {shopVariations(13, 'Auriga', true)}
            {inHaapsalu && (
              <tr>
                <td className="text-rgreen text-xs text-left sticky z-9998 pl-2 pr-4 pt-4 pb-2 left-0 font-axi-bold">
                  Haapsalu
                </td>
              </tr>
            )}
            {shopVariations(10, 'Rannarootsi', true)}
          </>
        </tbody>
      </table>
    </div>
  );
});

export default Availability;
